import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import api from "../api/client";
import type { Share } from "../types";

export function Shares() {
  useEffect(() => {
    document.title = "vibe-check ⏵Shares";
  }, []);
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [showActive, setShowActive] = useState(true);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const limit = 20;

  useEffect(() => {
    loadShares(true);
  }, [showActive]);

  const loadShares = async (reset = false) => {
    try {
      setLoading(true);
      const newOffset = reset ? 0 : offset;
      const data = await api.getShares(limit, newOffset);

      // Filter by active status client-side (API returns all)
      const filtered = data.shares.filter((s) =>
        showActive ? s.is_active : !s.is_active,
      );

      if (reset) {
        setShares(filtered);
        setOffset(limit);
      } else {
        setShares((prev) => [...prev, ...filtered]);
        setOffset(newOffset + limit);
      }

      setTotal(data.total);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load shares");
    } finally {
      setLoading(false);
    }
  };

  const hasMore = offset < total;

  const getShareUrl = (share: Share) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/s/${share.slug || share.share_token}`;
  };

  const copyToClipboard = async (share: Share) => {
    const url = getShareUrl(share);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleRevoke = async (share: Share) => {
    if (
      !confirm(
        "Are you sure you want to revoke this share? The link will stop working.",
      )
    ) {
      return;
    }

    try {
      setDeletingId(share.id);
      await api.deleteShare(share.id);
      // Update local state
      setShares((prev) =>
        prev.map((s) => (s.id === share.id ? { ...s, is_active: false } : s)),
      );
      // If viewing active shares, remove from list
      if (showActive) {
        setShares((prev) => prev.filter((s) => s.id !== share.id));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke share");
    } finally {
      setDeletingId(null);
    }
  };

  const getScopeLabel = (share: Share) => {
    switch (share.scope_type) {
      case "all":
        return "All Messages";
      case "session":
        return `Session #${share.scope_session_id?.slice(0, 8) || "?"}`;
      case "repository":
        const repoMatch = share.scope_git_remote_url?.match(/\/([^/]+)\.git$/);
        return `Repo: ${repoMatch ? repoMatch[1] : share.scope_git_remote_url || "?"}`;
      case "conversation":
        return `File: ${share.scope_file_name?.split("/").pop() || "?"}`;
      case "message":
        return "Single Message";
      default:
        return share.scope_type;
    }
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case "public":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case "private":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        );
      case "users":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  if (error && shares.length === 0) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => loadShares(true)}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">My Shares</h1>
        <Link
          to="/messages"
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center space-x-2"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          <span>Create Share</span>
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex space-x-2">
        <button
          onClick={() => setShowActive(true)}
          className={`px-4 py-2 rounded ${
            showActive
              ? "bg-purple-600 text-white"
              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setShowActive(false)}
          className={`px-4 py-2 rounded ${
            !showActive
              ? "bg-purple-600 text-white"
              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
          }`}
        >
          Revoked
        </button>
      </div>

      {/* Shares list */}
      <div className="space-y-4">
        {shares.map((share) => (
          <div
            key={share.id}
            className={`bg-gray-800 rounded-lg p-4 border ${
              share.is_active
                ? "border-gray-700"
                : "border-gray-700/50 opacity-60"
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-medium text-white">
                  {share.title || getScopeLabel(share)}
                </h3>
                {share.title && (
                  <p className="text-sm text-gray-400">
                    {getScopeLabel(share)}
                  </p>
                )}
              </div>
              {!share.is_active && (
                <span className="px-2 py-1 bg-red-900/30 text-red-400 text-xs rounded">
                  Revoked
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mb-4">
              <span className="flex items-center gap-1">
                {getVisibilityIcon(share.visibility)}
                {share.visibility}
              </span>
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                {share.view_count} views
              </span>
              <span>
                Created {format(parseISO(share.created_at), "MMM d, yyyy")}
              </span>
              {share.expires_at && (
                <span className="text-yellow-400">
                  Expires {format(parseISO(share.expires_at), "MMM d, yyyy")}
                </span>
              )}
            </div>

            {share.description && (
              <p className="text-sm text-gray-300 mb-4">{share.description}</p>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => copyToClipboard(share)}
                disabled={!share.is_active}
                className="px-3 py-1.5 bg-gray-700 text-white text-sm rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {copiedId === share.id ? (
                  <>
                    <svg
                      className="w-4 h-4 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                      />
                    </svg>
                    Copy Link
                  </>
                )}
              </button>

              <Link
                to={`/s/${share.slug || share.share_token}`}
                className="px-3 py-1.5 bg-gray-700 text-white text-sm rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                View
              </Link>

              {share.is_active && (
                <button
                  onClick={() => handleRevoke(share)}
                  disabled={deletingId === share.id}
                  className="px-3 py-1.5 bg-red-900/30 text-red-400 text-sm rounded hover:bg-red-900/50 disabled:opacity-50 flex items-center gap-1"
                >
                  {deletingId === share.id ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                      Revoking...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Revoke
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}

        {shares.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-400">
              {showActive ? "No active shares" : "No revoked shares"}
            </p>
            {showActive && (
              <p className="text-sm text-gray-500 mt-2">
                Go to{" "}
                <Link
                  to="/messages"
                  className="text-purple-400 hover:underline"
                >
                  Messages
                </Link>{" "}
                to create a share
              </p>
            )}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        )}

        {hasMore && !loading && shares.length > 0 && (
          <div className="flex justify-center">
            <button
              onClick={() => loadShares()}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
